import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { Icon } from 'components/ui/index';
import 'components/ui/button.scss';

const ButtonTag = React.forwardRef(({ as, color, className, outline, ...rest }, ref) => {
  const colorClass = outline ? `btn-outline-${color}` : `btn-${color}`;
  const btnClasses = classNames('btn', colorClass, className);

  switch (as) {
    case 'button':
      return <button ref={ref} type="button" className={btnClasses} {...rest} />;
    case 'a':
      return <a href={rest.href} ref={ref} className={btnClasses} {...rest} />;
    case 'nav-link':
      return <NavLink ref={ref} className={btnClasses} {...rest} />;
    case 'text':
      return <a ref={ref} className={classNames('text-decoration-none cursor-pointer', `text-${color}`, className)} {...rest} />;
    default:
  }
});

const Button = React.forwardRef(
  ({ children, loading, disabled, as, icon, iconAlign, iconClassName, className, size, noBorder, outline, ...otherProps }, ref) => {
    return (
      <ButtonTag
        as={as}
        ref={ref}
        outline={outline}
        className={classNames('app-button', className, size ? `btn-${size}` : false, {
          'no-border': noBorder,
        })}
        {...otherProps}
        disabled={loading || disabled}
      >
        {loading && <span className="btn-spinner" />}
        <span className={classNames('btn-content d-flex align-center', { 'is-loading': loading })}>
          {icon && (
            <div className="d-flex align-center">
              {iconAlign === 'right' && children}
              <Icon
                name={icon}
                className={classNames(iconClassName, {
                  'mr-1': children && iconAlign === 'left',
                  'ml-1': children && iconAlign === 'right',
                })}
              />
              {iconAlign === 'left' && children}
            </div>
          )}
          {!icon && children}
        </span>
      </ButtonTag>
    );
  },
);

Button.defaultProps = {
  loading: false,
  as: 'button',
  iconClassName: null,
  icon: null,
  iconAlign: 'left',
  size: 'md',
  children: null,
  noBorder: false,
  outline: false,
  className: '',
  disabled: false,
};

Button.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  noBorder: PropTypes.bool,
  outline: PropTypes.bool,
  as: PropTypes.oneOf(['button', 'a', 'nav-link', 'text']),
  size: PropTypes.oneOf(['sm', 'lg', 'md', 'tiny']),
  children: PropTypes.any,
  iconAlign: PropTypes.oneOf(['left', 'right']),
  icon: PropTypes.string,
  iconClassName: PropTypes.string,
  className: PropTypes.string,
};

export default Button;
